//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import LeaveConfirm from 'components/LeaveConfirm';
import TabGroup from 'components/TabGroup';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useCallbackPrompt } from 'hook/useCallbackPrompt';
import PortalProfile from 'pages/PortalProfile';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildVersionList, addSiteIdPrefix } from 'utils/helper';
import Members from './Members';
import MyAccount from './MyAccount';

export enum Role {
  Owner = 'owner',
  Manager = 'manager',
  Viewer = 'viewer',
}

const Profile = ({ tab }: { tab: number }) => {
  const { t } = useTranslation();
  const { buildVersion } = useConfig();
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation, requestConfirm] = useCallbackPrompt(showDialog);
  const addSiteIdPrefix_ = addSiteIdPrefix();
  const navigateUrl = [`${addSiteIdPrefix_}/profile/myAccount`, `${addSiteIdPrefix_}/profile/members`];
  const labels: { icon: string; name: string; width: string }[] = [
    { icon: '', name: t('profile.myAccountPageTitle'), width: '' },
    ...(user.role === Role.Owner ? [{ icon: '', name: t('profile.membersPageTitle'), width: '' }] : []),
  ];

  const handleTabSwitch = useCallback(
    async (tab: number, action: () => void) => {
      if (!showDialog) return true;
      requestConfirm(action);
      return false;
    },
    [showDialog, requestConfirm]
  );

  return (
    <>
      <div className="card-title px-4 py-4 bg-base-100 shadow flex justify-between">
        <h2>{t('profile.pageTitle')}</h2>
      </div>
      <TabGroup
        initIndex={tab}
        labels={labels.map((l) => (
          <div className={`flex items-center space-x-1 ${l.width}`}>
            <div className="text-sky-800">{l.name}</div>
          </div>
        ))}
        childrens={[
          ...(buildVersion === BuildVersionList.portal ? [<PortalProfile />] : [<MyAccount setShowDialog={setShowDialog} />]),
          <Members showDialog={showDialog} setShowDialog={setShowDialog} requestConfirm={requestConfirm} />,
        ]}
        tabClass="tab tab-bordered h-10 flex-auto"
        otherClass="flex-row-reverse"
        navigateUrl={navigateUrl}
        onClickPreFunction={handleTabSwitch}
      />
      <LeaveConfirm showDialog={showPrompt} confirmNavigation={confirmNavigation} cancelNavigation={cancelNavigation} />
    </>
  );
};

export default Profile;
